/*
 * @author BSG <dev@bsgroup.eu>
 * @copyright Better Software Group S.A.
 * @version: 1.0
 */
import { httpService, offeringIdsService } from "../../../../services";
import { HTTP_METHOD } from "@nf/constants";
import {
  IAssetEpisodeListModel,
  IAssetListModel,
  IAssetModel,
  IAssetQueryParameters,
  IAssetSeasonModel,
  ISearchListModel,
  ISearchMultiListModel,
} from "../models";
import { AppConfig } from "@nf/constants";

export class AssetService {
  get url(): string {
    return "/v1";
  }

  public get = async ({
    assetId,
    includeSeasons,
    includeEpisodes,
    includeCollections,
    parentalRatings,
  }: {
    assetId: string | number;
    includeSeasons?: boolean;
    includeEpisodes?: boolean;
    includeCollections?: boolean;
    parentalRatings?: string;
  }): Promise<IAssetModel> => {
    const params = {
      includeSeasons,
      includeEpisodes,
      includeCollections,
      parentalRatings,
      products: await offeringIdsService.getOfferingIds(),
    };
    return httpService.request<IAssetModel>({
      axiosConfig: {
        params,
      },
      method: HTTP_METHOD.GET,
      url: `${this.url}/content/asset/${assetId}`,
    });
  };

  public getNext = (assetId: string | number): Promise<IAssetModel> =>
    httpService.request<IAssetModel>({
      method: HTTP_METHOD.GET,
      url: `${this.url}/content/asset/${assetId}/next`,
    });

  public getPrev = (assetId: string | number): Promise<IAssetModel> =>
    httpService.request<IAssetModel>({
      method: HTTP_METHOD.GET,
      url: `${this.url}/content/asset/${assetId}/previous`,
    });

  public getCollectionEntries = (
    assetId: string | number,
    queryParams?: IAssetQueryParameters
  ): Promise<IAssetListModel> => {
    const params = {
      fieldSet: "ALL",
      pageNumber: 1,
      pageSize: 200,
      ...queryParams,
    };

    return httpService.request<IAssetListModel>({
      axiosConfig: {
        params,
      },
      method: HTTP_METHOD.GET,
      url: `${this.url}/content/asset/${assetId}/collectionentries`,
    });
  };

  public getNextCollectionEntry = (
    assetId: string | number,
    collectionId: string | number
  ): Promise<IAssetModel> =>
    httpService.request<IAssetModel>({
      method: HTTP_METHOD.GET,
      url: `${this.url}/content/asset/${collectionId}/collectionentries/${assetId}/next`,
    });

  public getPrevCollectionEntry = (
    assetId: string | number,
    collectionId: string | number
  ): Promise<IAssetModel> =>
    httpService.request<IAssetModel>({
      method: HTTP_METHOD.GET,
      url: `${this.url}/content/asset/${collectionId}/collectionentries/${assetId}/previous`,
    });

  public getNextPlaylistEntry = (
    assetId: string | number,
    playlistId: string | number
  ): Promise<IAssetModel> => {
    const params = {
      fieldSet: "ALL",
    };

    return httpService.request<IAssetModel>({
      axiosConfig: { params },
      method: HTTP_METHOD.GET,
      url: `${this.url}/content/asset/config/${AppConfig.ConfigId}/component/${playlistId}/asset/${assetId}`,
    });
  };

  public getSeason = (seasonId: string): Promise<IAssetSeasonModel> =>
    httpService.request<IAssetSeasonModel>({
      method: HTTP_METHOD.GET,
      url: `${this.url}/content/season/${seasonId}`,
    });

  public getSeasonEpisodes = (
    assetId: string | number,
    seasonNumber: number,
    pageNumber: number = 1,
    pageSize: number = 150
  ): Promise<IAssetEpisodeListModel> => {
    const params = {
      pageNumber,
      pageSize,
      fieldSet: "ALL",
    };

    return httpService.request<IAssetEpisodeListModel>({
      axiosConfig: { params },
      method: HTTP_METHOD.GET,
      url: `${this.url}/content/asset/${assetId}/season/${seasonNumber}/episode`,
    });
  };

  public recommended = (assetId: string | number): Promise<IAssetListModel> =>
    httpService.request<IAssetListModel>({
      method: HTTP_METHOD.GET,
      url: `${this.url}/recommend/watchNext/${assetId}`,
    });

  public search = async (
    pageSize: number = 50,
    pageNumber: number = 1,
    sort?: string,
    locales?: string,
    query?: string,
    parentalRatings?: string,
    people?: string
  ): Promise<ISearchListModel> => {
    const params = {
      pageSize,
      pageNumber,
      sort,
      query,
      locales,
      fieldSet: "ALL",
      parentalRatings,
      products: await offeringIdsService.getOfferingIds(),
      addParticipantIds: people,
    };
    return httpService.request<ISearchListModel>({
      axiosConfig: {
        params,
      },
      method: HTTP_METHOD.GET,
      url: `/v3/content/search/asset/query/${query}`,
    });
  };

  public searchMulti = async (
    pageSize: number = 30,
    pageNumber: number = 1,
    locale?: string,
    query?: string,
    parentalRatings?: string
  ): Promise<ISearchMultiListModel> => {
    const params = {
      pageSize,
      pageNumber,
      query,
      locale,
      fieldSet: "ALL",
      parentalRatings,
      products: await offeringIdsService.getOfferingIds(),
    };
    return httpService.request<ISearchMultiListModel>({
      axiosConfig: {
        params,
      },
      method: HTTP_METHOD.GET,
      url: `/v3/content/search/query/${query}`,
    });
  };

  public assetsList = ({
    assetIds,
    ...params
  }: IAssetQueryParameters): Promise<IAssetListModel> => {
    return httpService.request<IAssetListModel>({
      axiosConfig: {
        params: {
          assetIds: assetIds?.join(","),
          fieldSet: "ALL",
          pageSize: 50,
          pageNumber: 1,
          ...params,
        },
      },
      method: HTTP_METHOD.GET,
      url: `${this.url}/content/asset`,
    });
  };

  public searchV1 = async (
    pageSize: number = 50,
    pageNumber: number = 1,
    query?: string,
    sort?: string,
    assetTypes?: string,
    parentalRatings?: string
  ): Promise<IAssetListModel> => {
    const params = {
      pageSize,
      pageNumber,
      query,
      sort,
      assetTypes,
      fieldSet: "ALL",
      parentalRatings,
      products: await offeringIdsService.getOfferingIds(),
    };
    return httpService.request<IAssetListModel>({
      axiosConfig: {
        params,
      },
      method: HTTP_METHOD.GET,
      url: `${this.url}/content/asset`,
    });
  };

  public searchByUrl = async (
    url: string,
    parentalRatings?: string
  ): Promise<IAssetListModel> => {
    let params: { parentalRatings?: string; products?: string } = {};

    if (parentalRatings) {
      params.parentalRatings = parentalRatings;
    }

    params = await offeringIdsService.includeProductsIdsParam(params);

    return httpService.request<IAssetListModel>({
      axiosConfig: { params },
      method: HTTP_METHOD.GET,
      url: url,
    });
  };
}
